import {
  useEffect,
  useRef,
  useState,
  ChangeEvent,
  FocusEvent,
  MouseEvent,
} from "react";
import { CountryCode } from "libphonenumber-js";
import Signature from "./Components/Signature";
import Logo from "./Components/Logo";
import PhoneField from "./Components/PhoneField";
import BannerSelect from "./Components/BannerSelect";
import { DEFAULT_COUNTRY_CODE } from "./data/countries";
import { formatInternationalPhone } from "./utils/phone";
import "./App.css";

export interface BannerProps {
  imageUrl: string;
  linkUrl: string;
}

export interface SignatureProps {
  fullName: string;
  position: string;
  phone?: string;
  banners?: Record<string, BannerProps>;
  selectedBannerType?: string;
}

type TextFieldName = "fullName" | "position";

interface FormErrors {
  fullName?: boolean;
  position?: boolean;
  banner?: boolean;
  phone?: boolean;
}

interface FormState {
  fullName: string;
  position: string;
  nationalNumber: string;
  countryCode: CountryCode;
  banners: Record<string, BannerProps>;
  selectedBannerType: string;
  copied: boolean;
  errors: FormErrors;
}

const BANNER_OPTIONS = [
  { value: "", text: "Choose a banner option" },
  { value: "productLaunchMay2026", text: "Product Launch May 2026" },
  {
    value: "industryReport",
    text: "2026 Attractions Industry Benchmark Report",
  },
  { value: "acquaPark", text: "GC Aqua Park Grows Online Reviews" },
  { value: "climbzone", text: "ClimbZone reduces payroll costs" },
] as const;

const DEFAULT_PREVIEW_BANNER = "productLaunchMay2026";

const initialState: FormState = {
  fullName: "",
  position: "",
  nationalNumber: "",
  countryCode: DEFAULT_COUNTRY_CODE,
  banners: {
    productLaunchMay2026: {
      imageUrl: "https://signature.rollersoftware.com/may-2026.png",
      linkUrl: "https://www.roller.software/product-launch/may-2026",
    },
    industryReport: {
      imageUrl:
        "https://signature.rollersoftware.com/Industry-Benchmark-Report-2026.png",
      linkUrl: "https://www.roller.software/2026-benchmark-report",
    },
    acquaPark: {
      imageUrl:
        "https://3375779.fs1.hubspotusercontent-na1.net/hubfs/3375779/EmailTemplates/Banner_Image_AquaPark@2x.png",
      linkUrl: "https://www.roller.software/blog/gc-aqua-park",
    },
    climbzone: {
      imageUrl:
        "https://3375779.fs1.hubspotusercontent-na1.net/hubfs/3375779/EmailTemplates/Banner_Image_Climbzone@2x.png",
      linkUrl: "https://www.roller.software/blog/case-study-climbzone",
    },
  },
  selectedBannerType: "",
  copied: false,
  errors: {},
};

async function copySignatureHtml(element: Element): Promise<boolean> {
  const html = element.outerHTML;
  const plain = element.textContent ?? "";

  if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([plain], { type: "text/plain" }),
        }),
      ]);
      return true;
    } catch {
      // Fall through to execCommand for clients that reject ClipboardItem
    }
  }

  const selection = window.getSelection();
  const range = document.createRange();
  range.selectNode(element);
  selection?.removeAllRanges();
  selection?.addRange(range);

  try {
    return document.execCommand("copy");
  } finally {
    selection?.removeAllRanges();
  }
}

const App = () => {
  const [state, setState] = useState<FormState>(initialState);
  const copiedTimeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    return () => {
      window.clearTimeout(copiedTimeoutRef.current);
    };
  }, []);

  const displayName = state.fullName.trim() || "Your name";
  const displayPosition = state.position.trim() || "Your title";
  const displayPhone =
    formatInternationalPhone(state.nationalNumber, state.countryCode) ||
    undefined;

  const handleTextChange = (event: ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name as TextFieldName;
    const { value } = event.target;
    setState((prev) => ({
      ...prev,
      [name]: value,
      errors: { ...prev.errors, [name]: false },
      copied: false,
    }));
  };

  const handleTextBlur = (event: FocusEvent<HTMLInputElement>) => {
    const name = event.target.name as TextFieldName;
    const cleaned = event.target.value.replace(/\s+/g, " ").trim();
    setState((prev) => ({
      ...prev,
      [name]: cleaned,
    }));
  };

  const copyToClipboard = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const fullName = state.fullName.replace(/\s+/g, " ").trim();
    const position = state.position.replace(/\s+/g, " ").trim();
    const errors: FormErrors = {};

    if (!fullName) errors.fullName = true;
    if (!position) errors.position = true;
    if (!state.selectedBannerType) errors.banner = true;

    if (Object.keys(errors).length > 0) {
      setState((prev) => ({
        ...prev,
        fullName,
        position,
        errors: { ...prev.errors, ...errors },
      }));
      return;
    }

    const signatureEl = document.querySelector(".signature");
    if (!signatureEl) {
      console.error("Signature element not found");
      return;
    }

    try {
      const successful = await copySignatureHtml(signatureEl);
      if (!successful) return;

      setState((prev) => ({ ...prev, copied: true, errors: {} }));
      window.clearTimeout(copiedTimeoutRef.current);
      copiedTimeoutRef.current = window.setTimeout(() => {
        setState((prev) => ({ ...prev, copied: false }));
      }, 1800);
    } catch (err) {
      console.error("Copy command failed", err);
    }
  };

  const clearState = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    window.clearTimeout(copiedTimeoutRef.current);
    setState(initialState);
  };

  return (
    <div className="min-h-screen w-full max-w-[100vw] overflow-x-hidden bg-white flex flex-col desktop:flex-row desktop:items-stretch font-sans">
      <section className="bg-roller-blue-10 text-white w-full min-w-0 max-w-full box-border desktop:flex-1 desktop:max-w-[600px] desktop:min-w-[400px] px-8 py-12 desktop:p-12 flex flex-col gap-10 desktop:gap-16 desktop:justify-between">
        <div className="flex flex-col gap-10 desktop:gap-16 w-full min-w-0 max-w-full">
          <Logo variant="light" />

          {/*
            Mobile (<640): single column stack
            Tablet (640–1119): intro | form side by side
            Desktop (≥1120): single column stack again
          */}
          <div className="w-full min-w-0 max-w-full flex flex-col gap-10 desktop:gap-16 min-[640px]:max-[1119px]:grid min-[640px]:max-[1119px]:grid-cols-2 min-[640px]:max-[1119px]:gap-x-8 min-[640px]:max-[1119px]:gap-y-12 min-[640px]:max-[1119px]:items-start">
            <div className="flex flex-col gap-4 min-w-0 max-w-full min-[640px]:max-[1119px]:col-start-1 min-[640px]:max-[1119px]:row-start-1 desktop:contents">
              <div className="flex flex-col gap-4 w-full min-w-0 max-w-full">
                <h1 className="m-0 font-extrabold text-2xl leading-[30px] min-[640px]:text-[32px] min-[640px]:leading-10 tracking-tight text-white break-words">
                  Getting started
                </h1>
                <p className="m-0 text-base leading-6 min-[640px]:text-lg min-[640px]:leading-[27px] text-white break-words max-w-full">
                  To add a signature to your email, enter your information on
                  the left, choose the banner type from the select field, and
                  then click the &quot;Copy to clipboard&quot; button. This will
                  copy all the necessary markup for you to add to your email
                  service.
                </p>
              </div>
              <p className="hidden min-[640px]:max-[1119px]:block m-0 mt-auto text-sm leading-[18px] text-white">
                © Copyright 2026. All rights reserved.
              </p>
            </div>

            <div className="flex flex-col gap-10 w-full min-w-0 max-w-full min-[640px]:max-[1119px]:col-start-2 min-[640px]:max-[1119px]:row-start-1 min-[640px]:max-[1119px]:row-span-2 desktop:contents">
              <div className="flex flex-col gap-6 w-full min-w-0 max-w-full">
                <h2 className="m-0 font-extrabold text-2xl leading-[30px] min-[640px]:text-[32px] min-[640px]:leading-10 tracking-tight text-white break-words">
                  Enter your details
                </h2>

                <div className="flex flex-col gap-6 w-full min-w-0 max-w-full">
                  <div className="flex flex-col gap-1 w-full min-w-0">
                    <label htmlFor="fullName" className="form-field-label">
                      Full name *
                    </label>
                    <div
                      className={`form-input-frame${
                        state.errors.fullName ? " is-error" : ""
                      }`}
                    >
                      <input
                        id="fullName"
                        name="fullName"
                        type="text"
                        autoComplete="name"
                        placeholder="e.g. John Smith"
                        value={state.fullName}
                        onChange={handleTextChange}
                        onBlur={handleTextBlur}
                        className="form-field-input"
                        aria-invalid={state.errors.fullName || undefined}
                      />
                    </div>
                    {state.errors.fullName ? (
                      <p className="form-field-error" role="alert">
                        Please add your name
                      </p>
                    ) : null}
                  </div>

                  <div className="flex flex-col gap-1 w-full min-w-0">
                    <label htmlFor="position" className="form-field-label">
                      Position *
                    </label>
                    <div
                      className={`form-input-frame${
                        state.errors.position ? " is-error" : ""
                      }`}
                    >
                      <input
                        id="position"
                        name="position"
                        type="text"
                        autoComplete="organization-title"
                        placeholder="e.g. Sales & Marketing Director"
                        value={state.position}
                        onChange={handleTextChange}
                        onBlur={handleTextBlur}
                        className="form-field-input"
                        aria-invalid={state.errors.position || undefined}
                      />
                    </div>
                    {state.errors.position ? (
                      <p className="form-field-error" role="alert">
                        Please add your position
                      </p>
                    ) : null}
                  </div>

                  <PhoneField
                    countryCode={state.countryCode}
                    nationalNumber={state.nationalNumber}
                    onCountryChange={(code) =>
                      setState((prev) => ({
                        ...prev,
                        countryCode: code,
                        copied: false,
                      }))
                    }
                    onNumberChange={(nationalNumber) =>
                      setState((prev) => ({
                        ...prev,
                        nationalNumber,
                        copied: false,
                      }))
                    }
                  />

                  <BannerSelect
                    options={BANNER_OPTIONS}
                    value={state.selectedBannerType}
                    onChange={(value) =>
                      setState((prev) => ({
                        ...prev,
                        selectedBannerType: value,
                        errors: { ...prev.errors, banner: false },
                        copied: false,
                      }))
                    }
                    error={state.errors.banner}
                    helperText="Please choose a banner option"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-4 w-full min-w-0 min-[640px]:flex-row min-[640px]:flex-wrap min-[640px]:items-center">
                <button
                  type="button"
                  onClick={copyToClipboard}
                  className="btn-primary"
                >
                  Copy to clipboard
                </button>
                <button
                  type="button"
                  onClick={clearState}
                  className="btn-secondary"
                >
                  Clear all
                </button>
                <span
                  className={`text-sm text-[#B7F7C9] transition-opacity ${
                    state.copied ? "opacity-100" : "opacity-0"
                  }`}
                  aria-live="polite"
                >
                  Copied to clipboard
                </span>
              </div>
            </div>
          </div>
        </div>

        <p className="m-0 text-xs leading-4 min-[640px]:text-sm min-[640px]:leading-[18px] text-white min-[640px]:max-[1119px]:hidden desktop:block">
          © Copyright 2026. All rights reserved.
        </p>
      </section> 
      <section className="bg-white flex-1 min-w-0 max-w-full w-full box-border p-8 desktop:p-12 flex flex-col gap-12 items-center justify-center overflow-x-hidden">
        <div className="w-full min-w-0 max-w-[600px] overflow-x-hidden">
          <Signature
            fullName={displayName}
            position={displayPosition}
            phone={displayPhone}
            banners={state.banners}
            selectedBannerType={
              state.selectedBannerType || DEFAULT_PREVIEW_BANNER
            }
          />
        </div>
      </section>
    </div>
  );
};

export default App;
